import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'react-hot-toast';
import { Plus, Pencil, Trash2, Save } from 'lucide-react';
import ConfirmDialog from '../components/ConfirmDialog';

const DSAPartnerProgramSuccessStoriesPage = () => {
    const [loading, setLoading] = useState(true);
    const [savingSettings, setSavingSettings] = useState(false);
    const [stories, setStories] = useState([]);
    const [settings, setSettings] = useState({
        section_title: '',
        section_subtitle: '',
        cta_text: '',
        cta_link: ''
    });

    // Modal State
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [editingStory, setEditingStory] = useState(null);
    const [modalData, setModalData] = useState({
        title: '', // Name
        description: '', // Testimonial
        icon_name: '', // Role
        gradient_from: '', // Location
        gradient_to: '5', // Rating
        display_order: 0,
        is_active: true
    });

    const [deleteConfirm, setDeleteConfirm] = useState({ isOpen: false, id: null });
    const loanType = 'dsa_success_stories';

    useEffect(() => {
        fetchData();
    }, []);

    const fetchData = async () => {
        try {
            setLoading(true);
            const [settingsRes, storiesRes] = await Promise.all([
                axios.get(`/api/admin/key-benefits/settings/${loanType}`),
                axios.get(`/api/admin/key-benefits/${loanType}`)
            ]);

            if (settingsRes.data.success) {
                setSettings(settingsRes.data.data || {
                    section_title: '',
                    section_subtitle: '',
                    cta_text: '',
                    cta_link: ''
                });
            }

            if (storiesRes.data.success) {
                setStories(storiesRes.data.data || []);
            }
        } catch (error) {
            console.error('Error fetching data:', error);
            toast.error('Failed to load data');
        } finally {
            setLoading(false);
        }
    };

    // Settings Handlers
    const handleSettingsChange = (e) => {
        const { name, value } = e.target;
        setSettings(prev => ({ ...prev, [name]: value }));
    };

    const saveSettings = async (e) => {
        e.preventDefault();
        setSavingSettings(true);
        try {
            await axios.put(`/api/admin/key-benefits/settings/${loanType}`, {
                ...settings
            });
            toast.success('Section settings saved');
        } catch (error) {
            console.error('Error saving settings:', error);
            toast.error('Failed to save settings');
        } finally {
            setSavingSettings(false);
        }
    };

    // Story Handlers
    const handleAddNew = () => {
        setEditingStory(null);
        setModalData({
            title: '',
            description: '',
            icon_name: '',
            gradient_from: '',
            gradient_to: '5',
            display_order: stories.length + 1,
            is_active: true
        });
        setIsModalOpen(true);
    };

    const handleEdit = (story) => {
        setEditingStory(story);
        setModalData({
            title: story.title,
            description: story.description || '',
            icon_name: story.icon_name,
            gradient_from: story.gradient_from,
            gradient_to: story.gradient_to,
            display_order: story.display_order,
            is_active: story.is_active
        });
        setIsModalOpen(true);
    };

    const handleDelete = (id) => {
        setDeleteConfirm({ isOpen: true, id });
    };

    const confirmDelete = async () => {
        try {
            await axios.delete(`/api/admin/key-benefits/${deleteConfirm.id}`);
            toast.success('Story deleted successfully');
            fetchData();
        } catch (error) {
            console.error('Error deleting story:', error);
            toast.error('Failed to delete story');
        } finally {
            setDeleteConfirm({ isOpen: false, id: null });
        }
    };

    const handleModalSubmit = async (e) => {
        e.preventDefault();
        try {
            const payload = { ...modalData, loan_type: loanType };

            if (editingStory) {
                await axios.put(`/api/admin/key-benefits/${editingStory.id}`, payload);
                toast.success('Story updated successfully');
            } else {
                await axios.post('/api/admin/key-benefits', payload);
                toast.success('Story added successfully');
            }

            setIsModalOpen(false);
            fetchData();
        } catch (error) {
            console.error('Error saving story:', error);
            toast.error('Failed to save story');
        }
    };

    if (loading) return <div className="p-8 text-center">Loading...</div>;

    return (
        <div className="space-y-8">
            <div className="flex justify-between items-center">
                <div>
                    <h1 className="text-2xl font-bold text-slate-800">Success Stories</h1>
                    <p className="text-slate-500">Manage partner testimonials</p>
                </div>
            </div>

            {/* Settings Section */}
            <div className="bg-white rounded-2xl p-6 shadow-sm border border-slate-100">
                <h2 className="text-lg font-bold text-slate-800 mb-4">Section Settings</h2>
                <form onSubmit={saveSettings} className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="md:col-span-2">
                        <label className="block text-sm font-semibold text-slate-700 mb-2">Section Title</label>
                        <input
                            type="text"
                            name="section_title"
                            value={settings.section_title}
                            onChange={handleSettingsChange}
                            className="w-full px-4 py-2 border border-slate-300 rounded-xl focus:ring-2 focus:ring-primary-500"
                            placeholder="Partner Success Stories"
                        />
                    </div>
                    <div className="md:col-span-2">
                        <label className="block text-sm font-semibold text-slate-700 mb-2">Section Subtitle</label>
                        <input
                            type="text"
                            name="section_subtitle"
                            value={settings.section_subtitle}
                            onChange={handleSettingsChange}
                            className="w-full px-4 py-2 border border-slate-300 rounded-xl focus:ring-2 focus:ring-primary-500"
                            placeholder="Hear from our successful partners"
                        />
                    </div>
                    <div className="md:col-span-2 flex justify-end">
                        <button
                            type="submit"
                            disabled={savingSettings}
                            className="flex items-center gap-2 bg-primary-600 text-white px-6 py-2 rounded-xl hover:bg-primary-700 transition-colors"
                        >
                            <Save size={18} />
                            {savingSettings ? 'Saving...' : 'Save Settings'}
                        </button>
                    </div>
                </form>
            </div>

            {/* Stories List */}
            <div className="space-y-4">
                <div className="flex justify-between items-center">
                    <h2 className="text-lg font-bold text-slate-800">Testimonials</h2>
                    <button
                        onClick={handleAddNew}
                        className="flex items-center gap-2 bg-primary-600 text-white px-4 py-2 rounded-xl hover:bg-primary-700 transition-colors"
                    >
                        <Plus size={18} /> Add New Story
                    </button>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {stories.map((story) => (
                        <div key={story.id} className="bg-white rounded-2xl p-6 shadow-sm border border-slate-100 hover:shadow-md transition-shadow">
                            <div className="flex justify-between items-start mb-4">
                                <div>
                                    <h3 className="font-bold text-slate-800">{story.title}</h3>
                                    <p className="text-sm text-slate-500">{story.icon_name} • {story.gradient_from}</p>
                                </div>
                                <div className="flex items-center gap-1 bg-yellow-50 px-2 py-1 rounded-lg">
                                    <span className="text-sm font-bold text-yellow-700">★ {story.gradient_to}</span>
                                </div>
                            </div>
                            <p className="text-slate-600 mb-4 italic">"{story.description}"</p>
                            <div className="flex justify-between items-center pt-4 border-t border-slate-100">
                                <span className={`text-xs font-medium px-2 py-1 rounded-lg ${story.is_active ? 'bg-green-100 text-green-700' : 'bg-slate-100 text-slate-600'}`}>
                                    {story.is_active ? 'Active' : 'Inactive'}
                                </span>
                                <div className="flex gap-2">
                                    <button onClick={() => handleEdit(story)} className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg">
                                        <Pencil size={16} />
                                    </button>
                                    <button onClick={() => handleDelete(story.id)} className="p-2 text-red-600 hover:bg-red-50 rounded-lg">
                                        <Trash2 size={16} />
                                    </button>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            </div>

            {/* Edit/Add Modal */}
            {isModalOpen && (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
                    <div className="bg-white rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto p-6">
                        <h2 className="text-xl font-bold mb-6">{editingStory ? 'Edit Story' : 'Add New Story'}</h2>
                        <form onSubmit={handleModalSubmit} className="space-y-4">
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-sm font-medium mb-1">Name</label>
                                    <input
                                        type="text"
                                        value={modalData.title}
                                        onChange={(e) => setModalData({ ...modalData, title: e.target.value })}
                                        className="w-full px-4 py-2 border rounded-xl"
                                        required
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium mb-1">Role</label>
                                    <input
                                        type="text"
                                        value={modalData.icon_name}
                                        onChange={(e) => setModalData({ ...modalData, icon_name: e.target.value })}
                                        className="w-full px-4 py-2 border rounded-xl"
                                        placeholder="e.g. DSA Partner"
                                        required
                                    />
                                </div>
                            </div>
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-sm font-medium mb-1">Location</label>
                                    <input
                                        type="text"
                                        value={modalData.gradient_from}
                                        onChange={(e) => setModalData({ ...modalData, gradient_from: e.target.value })}
                                        className="w-full px-4 py-2 border rounded-xl"
                                        placeholder="e.g. Mumbai"
                                        required
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium mb-1">Rating (1-5)</label>
                                    <select
                                        value={modalData.gradient_to}
                                        onChange={(e) => setModalData({ ...modalData, gradient_to: e.target.value })}
                                        className="w-full px-4 py-2 border rounded-xl"
                                    >
                                        {[1, 2, 3, 4, 5].map(num => (
                                            <option key={num} value={num}>{num}</option>
                                        ))}
                                    </select>
                                </div>
                            </div>
                            <div>
                                <label className="block text-sm font-medium mb-1">Testimonial</label>
                                <textarea
                                    value={modalData.description}
                                    onChange={(e) => setModalData({ ...modalData, description: e.target.value })}
                                    className="w-full px-4 py-2 border rounded-xl"
                                    rows={4}
                                    required
                                />
                            </div>

                            <div className="flex items-center gap-4">
                                <div>
                                    <label className="block text-sm font-medium mb-1">Display Order</label>
                                    <input
                                        type="number"
                                        value={modalData.display_order}
                                        onChange={(e) => setModalData({ ...modalData, display_order: parseInt(e.target.value) })}
                                        className="w-full px-4 py-2 border rounded-xl"
                                    />
                                </div>
                                <div className="flex items-center h-full pt-6">
                                    <label className="flex items-center gap-2 cursor-pointer">
                                        <input
                                            type="checkbox"
                                            checked={modalData.is_active}
                                            onChange={(e) => setModalData({ ...modalData, is_active: e.target.checked })}
                                            className="w-5 h-5 rounded text-primary-600"
                                        />
                                        <span className="font-medium">Active</span>
                                    </label>
                                </div>
                            </div>

                            <div className="flex justify-end gap-3 pt-4">
                                <button
                                    type="button"
                                    onClick={() => setIsModalOpen(false)}
                                    className="px-6 py-2 border rounded-xl hover:bg-slate-50"
                                >
                                    Cancel
                                </button>
                                <button
                                    type="submit"
                                    className="px-6 py-2 bg-primary-600 text-white rounded-xl hover:bg-primary-700"
                                >
                                    Save
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            <ConfirmDialog
                isOpen={deleteConfirm.isOpen}
                onClose={() => setDeleteConfirm({ isOpen: false, id: null })}
                onConfirm={confirmDelete}
                title="Delete Story"
                message="Are you sure you want to delete this testimonial?"
            />
        </div>
    );
};

export default DSAPartnerProgramSuccessStoriesPage;
